import {
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon,
  AlternateEmailOutlined as AlternateEmailOutlinedIcon,
  ChevronLeft as ChevronLeftIcon,
  CodeOutlined as CodeOutlinedIcon,
  EngineeringOutlined as EngineeringOutlinedIcon,
  ExtensionOutlined as ExtensionOutlinedIcon,
  FactoryOutlined as FactoryOutlinedIcon,
  InsightsOutlined as InsightsOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
  MailOutlined as MailOutlinedIcon,
  MemoryOutlined as MemoryOutlinedIcon,
  Menu as MenuIcon,
  MonitorHeartOutlined as MonitorHeartOutlinedIcon,
  PersonOutlined as PersonOutlinedIcon,
  PhoneAndroidOutlined as PhoneAndroidOutlinedIcon,
  PodcastsOutlined as PodcastsOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  ScheduleOutlined as ScheduleOutlinedIcon,
  SettingsInputAntennaOutlined as SettingsInputAntennaOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  SystemUpdateOutlined as SystemUpdateOutlinedIcon,
  TerminalOutlined as TerminalOutlinedIcon,
  TopicOutlined as TopicOutlinedIcon,
  WorkHistoryOutlined as WorkHistoryOutlinedIcon,
} from "@mui/icons-material";
import { Drawer, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { FC, ReactElement, useContext, useState } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import React from "react";
import {
  type ConfigSidebarGroup,
  type ConfigSidebarSection,
  ConfigurationSidebar,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from "./configuration/ConfigurationSidebar";
import AppReleases from "./configuration/pages/appReleases";
import type { AppReleaseApp } from "./configuration/pages/appReleases/models/AppRelease";
import Asset from "./configuration/pages/asset";
import ClientTelemetry from "./configuration/pages/clientTelemetry";
import EmailAddresses from "./configuration/pages/emailAddresses";
import EmailTopics from "./configuration/pages/emailTopics";
import Jobs from "./configuration/pages/job";
import Logs from "./configuration/pages/log";
import Operators from "./configuration/pages/operators";
import Profiles from "./configuration/pages/profiles";
import QueryCachePage from "./configuration/pages/query-cache";
import ChatComponent from "./configuration/pages/realtime/client";
import QueryCacheInvalidations from "./configuration/pages/realtime/config";
import Settings from "./configuration/pages/settings";
import ShiftsCrews from "./configuration/pages/shifCrew";
import Users from "./configuration/pages/users";
import {
  AppReleasesFeatureContext,
  ConfigurationContext,
  EmailTopicsFeatureContext,
  OperatorsFeatureContext,
} from "./globalContext";
import { SPExecutorPage } from "./pages/sp-executor";

export type {
  ConfigSidebarItem,
  ConfigSidebarSection,
} from "./configuration/ConfigurationSidebar";

export const emailTopicsUrl = "/configuration/email-topics";

export const MESF_CONFIGURATIONS = [
  "shifts",
  "asset",
  "operators",
  "users",
  "profiles",
  "logs",
  "client-telemetry",
  "jobs",
  "settings",
  "email-topics",
  "email-addresses",
  "app-releases",
  "real-time",
  "query-cache",
  "sp-executor",
];

interface SectionComponent {
  path: string;
  sidebar: FC;
  main: FC;
  exact?: boolean;
}

export type SidebarType = FC<{ option?: string }>;

export type UseSidebarSections = () => ConfigSidebarSection[] | undefined;

export type ConfigurationsType = [
  SectionComponent[],
  SidebarType | null,
  UseSidebarSections?,
];

const COLLAPSED_STORAGE_KEY = "mesf.configuration.sidebarCollapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const useNoSidebarSections: UseSidebarSections = () => undefined;

const ConfigurationRootRedirect: FC = () => <Navigate to="users" />;

const Configuration: FC = (): ReactElement => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { "*": currentPath = "" } = useParams();

  const [
    customConfiguration,
    CustomSidebar,
    useCustomSections = useNoSidebarSections,
  ] = useContext<ConfigurationsType>(ConfigurationContext);

  const operatorsFeature = useContext<boolean>(OperatorsFeatureContext);

  const emailTopicsFeature = useContext<boolean>(EmailTopicsFeatureContext);

  const appReleaseApps = useContext<AppReleaseApp[]>(AppReleasesFeatureContext);
  const appReleasesFeature = appReleaseApps.length > 0;

  const customSections = useCustomSections();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
    } catch {}
  };

  const routes: Array<SectionComponent> = [
    {
      path: "/",
      sidebar: (): ReactElement => <div>Configuration</div>,
      main: ConfigurationRootRedirect,
    },
    {
      path: "shifts",
      sidebar: (): ReactElement => <div>Shifts</div>,
      main: ShiftsCrews,
    },
    {
      path: "asset",
      sidebar: (): ReactElement => <div>Configuration Asset</div>,
      main: Asset,
    },
    ...(operatorsFeature
      ? [
          {
            path: "operators/*",
            sidebar: (): ReactElement => <div>Operators</div>,
            main: Operators,
          },
        ]
      : []),
    {
      path: "users",
      sidebar: (): ReactElement => <div>Users</div>,
      main: Users,
    },
    {
      path: "profiles",
      sidebar: (): ReactElement => <div>profiles</div>,
      main: Profiles,
    },
    {
      path: "logs",
      sidebar: (): ReactElement => <div>Logs</div>,
      main: Logs,
    },
    {
      path: "client-telemetry",
      sidebar: (): ReactElement => <div>Client Telemetry</div>,
      main: ClientTelemetry,
    },
    {
      path: "jobs",
      sidebar: (): ReactElement => <div>Jobs</div>,
      main: Jobs,
    },
    {
      path: "settings",
      sidebar: (): ReactElement => <div>Settings</div>,
      main: Settings,
    },
    ...(emailTopicsFeature
      ? [
          {
            path: "email-topics/*",
            sidebar: (): ReactElement => <div>Email Topics</div>,
            main: EmailTopics,
          },
          {
            path: "email-addresses/*",
            sidebar: (): ReactElement => <div>Email Addresses</div>,
            main: EmailAddresses,
          },
        ]
      : []),
    ...(appReleasesFeature
      ? [
          {
            path: "app-releases/*",
            sidebar: (): ReactElement => <div>App Releases</div>,
            main: AppReleases,
          },
        ]
      : []),
    {
      path: "real-time/client",
      sidebar: (): ReactElement => <div>Client</div>,
      main: ChatComponent,
    },
    {
      path: "real-time/config",
      sidebar: (): ReactElement => <div>Config</div>,
      main: QueryCacheInvalidations,
    },
    {
      path: "query-cache/config",
      sidebar: (): ReactElement => <div>Config</div>,
      main: QueryCachePage,
    },
    {
      path: "sp-executor",
      sidebar: (): ReactElement => <div>SP Executor</div>,
      main: SPExecutorPage,
    },
    ...customConfiguration,
  ];

  const leadingSections: ConfigSidebarSection[] = [
    {
      name: "Plant",
      icon: <FactoryOutlinedIcon />,
      items: [
        {
          path: "shifts",
          label: "Shift / Crews",
          icon: <ScheduleOutlinedIcon />,
        },
        ...(operatorsFeature
          ? [
              {
                path: "operators",
                label: "Operators",
                icon: <EngineeringOutlinedIcon />,
              },
            ]
          : []),
        {
          path: "asset",
          label: "Assets Tree",
          icon: <AccountTreeOutlinedIcon />,
        },
      ],
    },
    {
      name: "Security",
      icon: <LockOutlinedIcon />,
      items: [
        { path: "users", label: "Users", icon: <PersonOutlinedIcon /> },
        {
          path: "profiles",
          label: "Profiles",
          icon: <AdminPanelSettingsOutlinedIcon />,
        },
      ],
    },
    ...(emailTopicsFeature
      ? [
          {
            name: "Emails",
            icon: <MailOutlinedIcon />,
            items: [
              {
                path: "email-topics",
                label: "Email Topics",
                icon: <TopicOutlinedIcon />,
              },
              {
                path: "email-addresses",
                label: "Email Addresses",
                icon: <AlternateEmailOutlinedIcon />,
              },
            ],
          },
        ]
      : []),
    ...(appReleasesFeature
      ? [
          {
            name: "Mobile App",
            icon: <PhoneAndroidOutlinedIcon />,
            items: [
              {
                path: "app-releases",
                label: "App Releases",
                icon: <SystemUpdateOutlinedIcon />,
              },
            ],
          },
        ]
      : []),
  ];

  const trailingSections: ConfigSidebarSection[] = [
    {
      name: "Maintenance",
      icon: <MonitorHeartOutlinedIcon />,
      items: [
        { path: "logs", label: "Logs", icon: <ReceiptLongOutlinedIcon /> },
        {
          path: "client-telemetry",
          label: "Client Telemetry",
          icon: <InsightsOutlinedIcon />,
        },
        { path: "jobs", label: "Jobs", icon: <WorkHistoryOutlinedIcon /> },
        {
          path: "settings",
          label: "Settings",
          icon: <SettingsOutlinedIcon />,
        },
      ],
    },
    {
      name: "Developer",
      icon: <CodeOutlinedIcon />,
      items: [
        {
          path: "real-time/client",
          label: "Real Time Client",
          icon: <PodcastsOutlinedIcon />,
        },
        {
          path: "real-time/config",
          label: "Real Time Config",
          icon: <SettingsInputAntennaOutlinedIcon />,
        },
        {
          path: "query-cache/config",
          label: "Query Cache",
          icon: <MemoryOutlinedIcon />,
        },
        {
          path: "sp-executor",
          label: "SP Executor",
          icon: <TerminalOutlinedIcon />,
        },
      ],
    },
  ];

  const legacyGroups: ConfigSidebarGroup[] = CustomSidebar
    ? [
        {
          name: "More",
          icon: <ExtensionOutlinedIcon />,
          items: [],
          content: <CustomSidebar option={currentPath.split("/")[0]} />,
        },
      ]
    : [];

  const groups: ConfigSidebarGroup[] = [
    ...leadingSections,
    ...(customSections ?? legacyGroups),
    ...trailingSections,
  ];

  const railCollapsed = !isMobile && collapsed;
  const drawerWidth = railCollapsed
    ? SIDEBAR_COLLAPSED_WIDTH
    : SIDEBAR_EXPANDED_WIDTH;
  const widthTransition = theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shorter,
  });

  return (
    <div style={{ display: "flex", position: "relative" }}>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: isMobile ? 0 : drawerWidth,
          flexShrink: 0,
          transition: widthTransition,
        }}
        PaperProps={{
          sx: {
            width: drawerWidth,
            height: "calc(100vh - 64px)",
            top: 64,
            zIndex: 1200,
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.background.paper,
            transition: widthTransition,
          },
        }}
      >
        <ConfigurationSidebar
          groups={groups}
          currentPath={currentPath}
          collapsed={railCollapsed}
          onToggle={isMobile ? undefined : toggleCollapsed}
          onNavigate={() => setDrawerOpen(false)}
        />
      </Drawer>

      <main
        style={{
          flexGrow: 1,
          minWidth: 0,
          padding: theme.spacing(3),
        }}
      >
        <Routes>
          {routes.map((route) => (
            <Route
              key={`${route.path}`}
              path={route.path}
              element={
                <>
                  {isMobile && (
                    <IconButton
                      onClick={() => setDrawerOpen(!drawerOpen)}
                      style={{
                        position: "relative",
                        left: 0,
                        top: -10,
                        zIndex: 1100,
                        borderRadius: "0 4px 4px 0",
                        color: theme.palette.primary.main,
                        backgroundColor: theme.palette.background.paper,
                      }}
                    >
                      {drawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
                    </IconButton>
                  )}
                  <route.main />
                </>
              }
            />
          ))}
        </Routes>
      </main>
    </div>
  );
};

export default Configuration;
